/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Sampling tab for the Expense Inventory detail view. Mirrors the structure of
 * the Parts tab used on Orders Usage Details: a header bar with an icon/count
 * and Add/Search actions, plus a grid of the parts added.
 *
 * @module src/components/Inventory/expense/detail/ExpenseInventorySampling
 */
import React, {
  ForwardRefRenderFunction,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import PageHeader from '@salesforce/design-system-react/components/page-header';
import PageHeaderControl from '@salesforce/design-system-react/components/page-header/control';
import ButtonGroup from '@salesforce/design-system-react/components/button-group';
import Dropdown from '@salesforce/design-system-react/components/menu-dropdown';
import Button from '@salesforce/design-system-react/components/button';
import CartComponent from '../../../../Usage/UsageDetails/UsageTabs/Parts/CartComponent';
import { INVENTORY_CHANNELS } from '../../../../Usage/UsageDetails/UsageTabs/Parts/AddParts/useParts';
import {
  EXPENSE_INVENTORY_ASYNC_MESSAGES,
  EXPENSE_INVENTORY_DETAIL_UI,
  EXPENSE_INVENTORY_WORKFLOW_ACTION_OPTIONS,
  MAX_QUANTITY,
  NOTIFY_TYPE,
  WORKFLOW_ACTION,
} from '../../../../../util/constants';
import { getUSDString } from '../../../../../util/utilityFunctions';
import useSnackBar from '../../../../../util/customHooks/useSnackBar';
import SnackBar from '../../../../Shared/SnackBar';
import ExpenseInventoryAddParts from './AddPartsModal/ExpenseInventoryAddParts';
import ExpenseInventorySamplingTable, { SamplingLineItem } from './ExpenseInventorySamplingTable';
import useDeleteExpenseInventoryLineItem from './hook/useDeleteExpenseInventoryLineItem';
import useUpdateExpenseInventoryLineItemLotNumber from './hook/useUpdateExpenseInventoryLineItemLotNumber';
import useUpdateExpenseInventoryLineItemQuantity from './hook/useUpdateExpenseInventoryLineItemQuantity';
import useReviewExpenseInventoryDecisions from './hook/useReviewExpenseInventoryDecisions';
import useExpenseInventoryCatalogLotOptions from './hook/useExpenseInventoryCatalogLotOptions';
import {
  CatalogLotOption,
  buildCatalogLotOptions,
  getSamplingMaxQuantity,
  isAddedFromCatalog,
} from './samplingLineRules';
import { ALL_SAMPLING_COLUMNS_VISIBLE, SamplingColumnVisibility } from './samplingColumnConfig';
import styles from './styles.module.scss';

interface Props {
  expenseInventoryDetail?: any;
  refetch: () => Promise<any>;
  viewOnly?: boolean;
  isOpsUser?: boolean;
  isReview?: boolean;
  columnVisibility?: SamplingColumnVisibility;
  onReviewStateChange?: (state: { allLinesDecided: boolean; loadingSaveDecision: boolean }) => void;
}

export interface ExpenseInventorySamplingHandle {
  submitReview: () => void;
  flushPendingTableSaves: () => Promise<void>;
}

const resolveWorkflowAction = (
  action: string | undefined,
  shouldDefaultToManual: boolean
): string => {
  if (action && action !== WORKFLOW_ACTION.NO_ACTION) {
    return action;
  }
  return shouldDefaultToManual ? WORKFLOW_ACTION.MANUAL : WORKFLOW_ACTION.NO_ACTION;
};

const mapDetailLineItemToSamplingLineItem = (
  line: any,
  index: number,
  shouldDefaultToManual: boolean
): SamplingLineItem => ({
  uniqueId: line.sfid || line.externalId || `existing-part-${index}`,
  sfid: line.sfid,
  externalId: line.externalId,
  productId: line.productId,
  productNumber: line.productNumber,
  productDescription: line.productDescription,
  baseorCapital: line.baseorCapital,
  lotNumber: line.lotSerial,
  originalLotNumber: line.lotSerial,
  quantity: Number(line.quantity) || 0,
  originalQuantity: Number(line.quantity) || 0,
  actualQuantity: Number(line.actualQuantity) || Number(line.quantity) || 0,
  sourceLocation: line.sourceLocation,
  subInventoryCode: line.subInventoryCode,
  maxQuantity: getSamplingMaxQuantity(line),
  expirationDate: line.expirationDate,
  iscatalog: line.sourceLocation === INVENTORY_CHANNELS.CATALOG,
  isAddedFromCatalog: isAddedFromCatalog(line),
  isValidLotSerialCombination: !!line.isValidLotSerialCombination,
  cost: line.cost,
  workflowAction: resolveWorkflowAction(line.action, shouldDefaultToManual),
});

const resolveCurrentDecision = (item: SamplingLineItem): string =>
  item.userworkflowAction?.[0]?.value || item.workflowAction || WORKFLOW_ACTION.NO_ACTION;

const ExpenseInventorySamplingComponent: ForwardRefRenderFunction<
  ExpenseInventorySamplingHandle,
  Props
> = (
  {
    expenseInventoryDetail,
    refetch,
    viewOnly = false,
    isOpsUser = false,
    isReview = false,
    columnVisibility = ALL_SAMPLING_COLUMNS_VISIBLE,
    onReviewStateChange,
  },
  ref
) => {
  const [isAddPartsOpen, setIsAddPartsOpen] = useState(false);
  const [items, setItems] = useState<SamplingLineItem[]>([]);
  const { open, notification, openSnackBar } = useSnackBar();
  const { onDeleteLineItem, loadingDelete, deletingUniqueId } = useDeleteExpenseInventoryLineItem({
    expenseInventoryDetail,
    refetch,
    openSnackBar,
  });
  const { onLotNumberSave, loadingSaveLotNumber } = useUpdateExpenseInventoryLineItemLotNumber({
    expenseInventoryDetail,
    refetch,
    openSnackBar,
  });
  const { onQuantitySave, loadingSaveQuantity } = useUpdateExpenseInventoryLineItemQuantity({
    expenseInventoryDetail,
    refetch,
    openSnackBar,
  });
  const {
    onSaveDecisions,
    loadingSaveDecision,
    savingDecisionUniqueIds,
  } = useReviewExpenseInventoryDecisions({
    expenseInventoryDetail,
    refetch,
    openSnackBar,
  });
  const { catalogLotRowsByProductNumber } = useExpenseInventoryCatalogLotOptions({
    expenseInventoryDetail,
    isReview,
    skip: viewOnly,
  });
  const usageDate = expenseInventoryDetail?.usageDate;
  const catalogLotOptionsByProductNumber = useMemo(
    () =>
      Object.entries(catalogLotRowsByProductNumber).reduce<Record<string, CatalogLotOption[]>>(
        (optionsByProduct, [productNumber, rows]) => ({
          ...optionsByProduct,
          [productNumber]: buildCatalogLotOptions(rows, usageDate),
        }),
        {}
      ),
    [catalogLotRowsByProductNumber, usageDate]
  );

  // Every save the table starts (quantity, lot and the page-load re-check) runs one at a time:
  // each one reserves the difference from the line's saved quantity, so two saves of one line in
  // flight together would both count from the same original. The table stays disabled until the
  // queue, refetches included, is empty.
  const itemsRef = useRef<SamplingLineItem[]>(items);
  itemsRef.current = items;
  const onQuantitySaveRef = useRef(onQuantitySave);
  onQuantitySaveRef.current = onQuantitySave;
  const onLotNumberSaveRef = useRef(onLotNumberSave);
  onLotNumberSaveRef.current = onLotNumberSave;
  const tableSaveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const [queuedTableSaveCount, setQueuedTableSaveCount] = useState(0);
  const pendingQuantitiesRef = useRef<Map<string, number>>(new Map());
  const pendingLotNumbersRef = useRef<Map<string, string>>(new Map());
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const enqueueTableSave = useCallback((save: () => Promise<void> | void): Promise<void> => {
    setQueuedTableSaveCount(count => count + 1);
    const onSettled = (): void => setQueuedTableSaveCount(count => count - 1);
    tableSaveQueueRef.current = tableSaveQueueRef.current.then(save).then(onSettled, onSettled);
    return tableSaveQueueRef.current;
  }, []);

  // Reads the row when its turn comes, so the save carries what the row holds then (a lot saved
  // just before it, the ids), with the total that was on screen when the change settled. It sends
  // the row's saved lot: a lot still being typed is saved by its own blur, never by this save.
  const saveSettledQuantity = useCallback((uniqueId: string, quantity: number): Promise<void> => {
    const row = itemsRef.current.find(item => item.uniqueId === uniqueId);
    if (!row || quantity === Number(row.originalQuantity ?? 0)) {
      return Promise.resolve();
    }
    return onQuantitySaveRef.current(
      { ...row, lotNumber: row.originalLotNumber, quantity, actualQuantity: quantity },
      quantity
    );
  }, []);

  // A catalog row's lot save sends the row's quantity too, so a row with both a lot and a quantity
  // pending is one request carrying both, with the settled total when there is one.
  const saveSettledLotNumber = useCallback(
    (uniqueId: string, lotNumber: string, quantity?: number): Promise<void> => {
      const row = itemsRef.current.find(item => item.uniqueId === uniqueId);
      if (!row) {
        return Promise.resolve();
      }
      const rowQuantity = quantity ?? Number(row.quantity ?? 0);
      return onLotNumberSaveRef.current(
        { ...row, quantity: rowQuantity, actualQuantity: rowQuantity },
        lotNumber
      );
    },
    []
  );

  const flushPendingTableSaves = useCallback((): Promise<void> => {
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    const pendingQuantities = new Map(pendingQuantitiesRef.current);
    const pendingLotNumbers = Array.from(pendingLotNumbersRef.current.entries());
    pendingQuantitiesRef.current.clear();
    pendingLotNumbersRef.current.clear();
    pendingLotNumbers.forEach(([uniqueId, lotNumber]) => {
      const quantity = pendingQuantities.get(uniqueId);
      pendingQuantities.delete(uniqueId);
      enqueueTableSave(() => saveSettledLotNumber(uniqueId, lotNumber, quantity));
    });
    pendingQuantities.forEach((quantity, uniqueId) =>
      enqueueTableSave(() => saveSettledQuantity(uniqueId, quantity))
    );
    return tableSaveQueueRef.current;
  }, [enqueueTableSave, saveSettledQuantity, saveSettledLotNumber]);

  // + and - change the number without ever focusing the field, so a change is saved once the
  // whole table settles. One timer for every line and both fields: a change anywhere pushes it
  // back, so no save (which disables the table) starts while any + or - is held, not even the lot
  // save a press triggers by blurring the lot field.
  const restartSettleTimer = (): void => {
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
    }
    settleTimerRef.current = setTimeout(
      flushPendingTableSaves,
      EXPENSE_INVENTORY_DETAIL_UI.SAMPLING_QUANTITY_SAVE_DELAY_MS
    );
  };

  // A lot being typed holds every save until its field is left: a save would disable the field
  // mid-word and its refetch would put the saved lot back. The lot field's blur restarts the timer.
  const holdSettleTimer = (): void => {
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  };

  const scheduleQuantitySave = (uniqueId: string, quantity: number): void => {
    pendingQuantitiesRef.current.set(uniqueId, quantity);
    restartSettleTimer();
  };

  const scheduleLotNumberSave = (uniqueId: string, lotNumber: string): void => {
    pendingLotNumbersRef.current.set(uniqueId, lotNumber);
    restartSettleTimer();
  };

  // Leaving the page still saves a change that hasn't settled yet.
  useEffect(
    () => (): void => {
      flushPendingTableSaves();
    },
    [flushPendingTableSaves]
  );

  useEffect(() => {
    const shouldDefaultToManual = !!(
      expenseInventoryDetail?.usedInPatientCare || expenseInventoryDetail?.marketingCollaboration
    );
    setItems(
      expenseInventoryDetail?.lineItems?.map((line: any, index: number) =>
        mapDetailLineItemToSamplingLineItem(line, index, shouldDefaultToManual)
      ) ?? []
    );
  }, [expenseInventoryDetail]);

  // The detail re-checks catalog lots on every load. A catalog line whose lot is now in inventory
  // is saved once, and the save turns it into that inventory line (Bill Only re-saves on load too).
  const catalogConversionAttemptedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (viewOnly) {
      return;
    }
    const matchedCatalogItems = items.filter(
      item =>
        item.iscatalog &&
        item.isValidLotSerialCombination &&
        !catalogConversionAttemptedRef.current.has(item.uniqueId)
    );
    if (!matchedCatalogItems.length) {
      return;
    }
    matchedCatalogItems.forEach(item => {
      catalogConversionAttemptedRef.current.add(item.uniqueId);
      enqueueTableSave(() => onLotNumberSave(item, item.lotNumber || ''));
    });
  }, [items, viewOnly, onLotNumberSave, enqueueTableSave]);

  const handlePartsAdded = (): void => {
    refetch();
  };

  // Looks the row up in the latest items, not this render's: a counter whose row was re-keyed by
  // a refetch can keep stepping from a press it never saw released, and must not keep pushing
  // back the table's save.
  const handleQuantityChange = (uniqueId: string, quantity: number): void => {
    const item = itemsRef.current.find(currentItem => currentItem.uniqueId === uniqueId);
    if (!item) {
      return;
    }
    // A typed value is clamped to the cap, as Bill Only does, so the save never asks for more.
    const cappedQuantity = Math.min(quantity, item.maxQuantity || MAX_QUANTITY);
    setItems(prevItems =>
      prevItems.map(prevItem =>
        prevItem.uniqueId === uniqueId
          ? { ...prevItem, quantity: cappedQuantity, actualQuantity: cappedQuantity }
          : prevItem
      )
    );
    scheduleQuantitySave(uniqueId, cappedQuantity);
  };

  const handleLotNumberChange = (uniqueId: string, lotNumber: string): void => {
    holdSettleTimer();
    setItems(prevItems =>
      prevItems.map(item => (item.uniqueId === uniqueId ? { ...item, lotNumber } : item))
    );
  };

  const settleLotNumber = (item: SamplingLineItem, lotNumber: string): void => {
    // A lot typed back to its saved value cancels a lot save still waiting for the table to settle.
    if (lotNumber === (item.originalLotNumber || '')) {
      pendingLotNumbersRef.current.delete(item.uniqueId);
      restartSettleTimer();
      return;
    }
    scheduleLotNumberSave(item.uniqueId, lotNumber);
  };

  const handleLotNumberBlur = (uniqueId: string): void => {
    const item = items.find(currentItem => currentItem.uniqueId === uniqueId);
    if (!item) {
      return;
    }
    settleLotNumber(item, item.lotNumber || '');
  };

  // A lot picked from the suggestions is a lot change like a typed one: it waits for the table to
  // settle, and the save decides whether the lot is in inventory.
  const handleLotNumberSelect = (uniqueId: string, lotNumber: string): void => {
    const item = itemsRef.current.find(currentItem => currentItem.uniqueId === uniqueId);
    if (!item) {
      return;
    }
    setItems(prevItems =>
      prevItems.map(prevItem =>
        prevItem.uniqueId === uniqueId ? { ...prevItem, lotNumber } : prevItem
      )
    );
    settleLotNumber(item, lotNumber);
  };

  const handleDelete = (uniqueId: string): void => {
    const item = items.find(currentItem => currentItem.uniqueId === uniqueId);
    if (item) {
      pendingQuantitiesRef.current.delete(uniqueId);
      pendingLotNumbersRef.current.delete(uniqueId);
      onDeleteLineItem(item);
    }
  };

  const handleDecisionChange = (
    uniqueId: string,
    decision: { label: string; value: string }[]
  ): void => {
    const item = items.find(currentItem => currentItem.uniqueId === uniqueId);
    // An added line that hasn't synced to Salesforce yet has no sfid and can't be saved
    // by the save-only call (see useReviewExpenseInventoryDecisions). Leave it undecided
    // locally too, so what the screen shows always matches what was actually persisted.
    if (item && !item.sfid) {
      openSnackBar(NOTIFY_TYPE.WARNING, EXPENSE_INVENTORY_ASYNC_MESSAGES.DECISION_SKIPPED_UNSYNCED);
      return;
    }
    const previousDecision = item?.userworkflowAction;
    setItems(prevItems =>
      prevItems.map(prevItem =>
        prevItem.uniqueId === uniqueId ? { ...prevItem, userworkflowAction: decision } : prevItem
      )
    );
    const decisionValue = decision?.[0]?.value;
    if (item && decisionValue) {
      onSaveDecisions([
        {
          uniqueId: item.uniqueId,
          sfid: item.sfid,
          externalId: item.externalId,
          decision: decisionValue,
        },
      ]).then(success => {
        // A rolled-back save-only transaction can refetch data identical to what's already
        // cached, which Apollo won't re-render on — restore this row locally so the screen
        // never keeps showing a decision that was never actually persisted.
        if (!success) {
          setItems(prevItems =>
            prevItems.map(prevItem =>
              prevItem.uniqueId === uniqueId
                ? { ...prevItem, userworkflowAction: previousDecision }
                : prevItem
            )
          );
        }
      });
    }
  };

  const handleApproveAllDecisions = (): void => {
    const approveDecision = EXPENSE_INVENTORY_WORKFLOW_ACTION_OPTIONS.filter(
      option => option.value === WORKFLOW_ACTION.APPROVE
    );
    // Only apply the decision locally to lines that have synced to Salesforce (have an
    // sfid); unsynced lines are excluded from the save-only call and must stay undecided
    // on screen so it doesn't appear saved when it wasn't.
    const previousDecisionsByUniqueId = new Map(
      items.filter(item => item.sfid).map(item => [item.uniqueId, item.userworkflowAction])
    );
    setItems(prevItems =>
      prevItems.map(item => (item.sfid ? { ...item, userworkflowAction: approveDecision } : item))
    );
    if (items.some(item => !item.sfid)) {
      openSnackBar(NOTIFY_TYPE.WARNING, EXPENSE_INVENTORY_ASYNC_MESSAGES.DECISION_SKIPPED_UNSYNCED);
    }
    onSaveDecisions(
      items.map(item => ({
        uniqueId: item.uniqueId,
        sfid: item.sfid,
        externalId: item.externalId,
        decision: WORKFLOW_ACTION.APPROVE,
      }))
    ).then(success => {
      // See handleDecisionChange: restore only the rows this call touched, by uniqueId,
      // rather than relying on a refetch to trigger a rebuild.
      if (!success) {
        setItems(prevItems =>
          prevItems.map(item =>
            previousDecisionsByUniqueId.has(item.uniqueId)
              ? { ...item, userworkflowAction: previousDecisionsByUniqueId.get(item.uniqueId) }
              : item
          )
        );
      }
    });
  };

  const handleRejectAllDecisions = (): void => {
    const rejectDecision = EXPENSE_INVENTORY_WORKFLOW_ACTION_OPTIONS.filter(
      option => option.value === WORKFLOW_ACTION.REJECT
    );
    // See handleApproveAllDecisions: skip unsynced lines locally too.
    const previousDecisionsByUniqueId = new Map(
      items.filter(item => item.sfid).map(item => [item.uniqueId, item.userworkflowAction])
    );
    setItems(prevItems =>
      prevItems.map(item => (item.sfid ? { ...item, userworkflowAction: rejectDecision } : item))
    );
    if (items.some(item => !item.sfid)) {
      openSnackBar(NOTIFY_TYPE.WARNING, EXPENSE_INVENTORY_ASYNC_MESSAGES.DECISION_SKIPPED_UNSYNCED);
    }
    onSaveDecisions(
      items.map(item => ({
        uniqueId: item.uniqueId,
        sfid: item.sfid,
        externalId: item.externalId,
        decision: WORKFLOW_ACTION.REJECT,
      }))
    ).then(success => {
      // See handleDecisionChange: restore only the rows this call touched, by uniqueId,
      // rather than relying on a refetch to trigger a rebuild.
      if (!success) {
        setItems(prevItems =>
          prevItems.map(item =>
            previousDecisionsByUniqueId.has(item.uniqueId)
              ? { ...item, userworkflowAction: previousDecisionsByUniqueId.get(item.uniqueId) }
              : item
          )
        );
      }
    });
  };

  const allLinesDecided =
    items.length > 0 &&
    items.every(item => resolveCurrentDecision(item) !== WORKFLOW_ACTION.NO_ACTION);

  const reviewSubmitInFlightRef = useRef(false);
  useImperativeHandle(
    ref,
    () => ({
      submitReview: (): void => {
        if (reviewSubmitInFlightRef.current) {
          return;
        }
        reviewSubmitInFlightRef.current = true;
        const releaseReviewSubmit = (): void => {
          reviewSubmitInFlightRef.current = false;
        };
        flushPendingTableSaves()
          .then(() =>
            onSaveDecisions(
              items.map(item => ({
                uniqueId: item.uniqueId,
                sfid: item.sfid,
                externalId: item.externalId,
                decision: resolveCurrentDecision(item),
              })),
              { submit: true }
            )
          )
          .then(releaseReviewSubmit, releaseReviewSubmit);
      },
      flushPendingTableSaves,
    }),
    [items, onSaveDecisions, flushPendingTableSaves]
  );

  useEffect(() => {
    // A queued quantity or lot save counts as saving, so the review Submit waits for it.
    onReviewStateChange?.({
      allLinesDecided,
      loadingSaveDecision: loadingSaveDecision || queuedTableSaveCount > 0,
    });
  }, [allLinesDecided, loadingSaveDecision, queuedTableSaveCount, onReviewStateChange]);

  const actions = (): ReactNode => (
    <PageHeaderControl>
      <ButtonGroup variant="list" id="sampling-header-button-group">
        <Dropdown
          disabled={viewOnly}
          iconCategory="utility"
          iconSize="small"
          align="right"
          iconName="add"
          iconPosition="left"
          label={<span data-testid="sampling-add-btn">Add</span>}
          onClick={(): void => setIsAddPartsOpen(true)}
        />
        <Button
          className="yellow-btn"
          label="Search"
          disabled={viewOnly}
          onClick={(): void => setIsAddPartsOpen(true)}
        />
      </ButtonGroup>
    </PageHeaderControl>
  );

  return (
    <IconSettings iconPath="/icons">
      <PageHeader
        className="slds-page-header-usage slds-page-header__name-title-inventory slds-m-around_small slds-m-bottom_none"
        onRenderActions={actions}
        title={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <span style={{ marginRight: '10px' }}>
              <CartComponent count={items.length} color="white" textColor="black" />
            </span>
            <span style={{ fontSize: '18px' }}>Sampling</span>
          </div>
        }
        variant="record-home"
        id="expense-inventory-sampling-header"
      />
      {isOpsUser && isReview && (
        <div className={styles.totalCostBar}>
          <span>Total Cost: {getUSDString(String(expenseInventoryDetail?.totalCost || 0))}</span>
        </div>
      )}
      <ExpenseInventorySamplingTable
        items={items}
        columnVisibility={columnVisibility}
        viewOnly={
          viewOnly ||
          loadingDelete ||
          loadingSaveLotNumber ||
          loadingSaveQuantity ||
          queuedTableSaveCount > 0
        }
        isOpsUser={isOpsUser}
        isReview={isReview}
        deletingUniqueId={deletingUniqueId}
        savingDecisionUniqueIds={savingDecisionUniqueIds}
        disableBulkDecisionActions={loadingSaveDecision}
        onQuantityChange={handleQuantityChange}
        onLotNumberChange={handleLotNumberChange}
        onLotNumberBlur={handleLotNumberBlur}
        onLotNumberSelect={handleLotNumberSelect}
        catalogLotOptionsByProductNumber={catalogLotOptionsByProductNumber}
        onDelete={handleDelete}
        onDecisionChange={handleDecisionChange}
        onApproveAllDecisions={handleApproveAllDecisions}
        onRejectAllDecisions={handleRejectAllDecisions}
      />
      {isAddPartsOpen && (
        <ExpenseInventoryAddParts
          isOpen={isAddPartsOpen}
          handleClose={(): void => setIsAddPartsOpen(false)}
          expenseInventoryDetail={expenseInventoryDetail}
          count={items.length}
          onPartsAdded={handlePartsAdded}
          isReview={isReview}
        />
      )}
      <SnackBar open={open} notification={notification} />
    </IconSettings>
  );
};

const ExpenseInventorySampling = forwardRef(ExpenseInventorySamplingComponent);

export default ExpenseInventorySampling;
